import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import DataHelper from "../../helpers/Data.js";
import { boxOptions, conditionOptions, yearOptions } from "../../product/constants.js";
import { currencyOptions, getBaseCurrency } from "../../core/currency.js";
import ReferenceSelect from "./ReferenceSelect.jsx";
import { createDictatedListing } from "../services/dictation.js";
import { Button, ButtonGroup } from "../../components/ui";
import "./dictation-review.scss";

/**
 * The editable review step, and the end of the flow: the listing is created
 * from here. The rep no longer passes through the create/edit form.
 *
 * Every list is the SAME source the listing form uses — DataHelper,
 * product/constants, core/currency — so the panel and the form cannot start
 * offering different ids for the same field. (The condition list used to be
 * inline in RCListingForm; it now lives in product/constants and is imported by
 * both.)
 *
 * The backend normalises spoken words to canonical ids and drops anything
 * outside the allowed set, so a key is either a valid id or absent. An absent
 * key leaves the control UNSELECTED and flagged as still needed — never
 * defaulted to a guess.
 */

const PLACEHOLDER = "— still needed —";

/** The six the panel asks for. Nothing else blocks creation — see onCreate. */
const MANDATORY = ["brand", "model", "reference", "condition", "box", "availability"];

const LABELS = {
  brand: "Brand",
  model: "Model",
  reference: "Reference",
  condition: "Condition",
  box: "Delivery set",
  availability: "Availability",
};

// Circle when empty, tick when filled: the shape carries the state, so the
// green is reinforcement rather than the only signal.
const Marker = ({ filled }) => (
  <span
    className={`rc-dictrev__marker${filled ? " is-filled" : ""}`}
    aria-hidden="true"
  >
    {filled ? "✓" : "○"}
  </span>
);

const FieldShell = ({ id, label, required, filled, missing, children }) => (
  <div className={`rc-dictrev__field${missing ? " is-missing" : ""}`}>
    <label className="rc-dictrev__label" htmlFor={id}>
      <Marker filled={filled} />
      {label}
      {required && (
        <sup className="rc-dictrev__req" aria-hidden="true">*</sup>
      )}
      {/* The marker and the asterisk are decorative; this is what a screen
          reader actually hears. */}
      <span className="rc-dictrev__sr">
        {required ? "required, " : ""}
        {filled ? "filled" : "still needed"}
      </span>
    </label>
    {children}
    {missing && (
      <span className="rc-dictrev__error" role="alert">
        {label} is needed before this can be created.
      </span>
    )}
  </div>
);

// Native <select> on purpose: it is the one control that behaves correctly at
// 320px on every phone without a custom popup that fights the page's scroll.
const Choice = ({ id, label, options, value, onChange, required = false, missing = false, fieldRef }) => (
  <FieldShell id={id} label={label} required={required} filled={!!value} missing={missing}>
    <select
      id={id}
      ref={fieldRef}
      className={`rc-dictrev__select${value ? "" : " is-empty"}`}
      value={value ?? ""}
      aria-required={required || undefined}
      aria-invalid={missing || undefined}
      onChange={(e) => onChange(e.target.value || undefined)}
    >
      <option value="">{PLACEHOLDER}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  </FieldShell>
);

const useOptions = (url) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let live = true;
    if (!url) {
      setOptions([]);
      return () => { live = false; };
    }
    axios
      .get(url)
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.data ?? [];
        if (live) setOptions(rows.map((r) => ({ id: String(r.id), name: r.name })));
      })
      .catch(() => { if (live) setOptions([]); });
    return () => { live = false; };
  }, [url]);
  return options;
};

const DictationReview = ({ result, onCreated, onSearchManually, onViewListing }) => {
  const category = DataHelper.WATCHES_CATEGORY_ID;
  const candidates = useMemo(() => result?.candidates ?? [], [result]);
  const loose = useMemo(() => result?.looseCandidates ?? [], [result]);
  const all = useMemo(() => [...candidates, ...loose], [candidates, loose]);

  const value = (key) => result?.fields?.[key]?.value;

  // Selection is never automatic: no candidate starts picked.
  const [picked, setPicked] = useState(null);
  // The API returns brand and model as NAMES (there is no brand_id on a
  // candidate), so the dropdowns are pre-selected by matching the heard name
  // against the catalogue lists once they load.
  const [brandId, setBrandId] = useState("");
  const [modelId, setModelId] = useState("");
  // A model named by a reference pick, waiting for that brand's model list.
  const [pendingModel, setPendingModel] = useState(null);
  const [draft, setDraft] = useState(() => ({
    condition: value("condition"),
    box: value("box"),
    availability: value("availability"),
    currency: value("currency") || getBaseCurrency(),
    year: value("year") != null ? String(value("year")) : undefined,
    // MAJOR units, and may arrive as a float (12000.0). Never multiplied here —
    // processSubmitValues does the ×100 on the way out.
    price: value("price") != null ? String(value("price")) : "",
  }));

  const [missing, setMissing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [created, setCreated] = useState(null);

  const refs = useRef({});
  const bindRef = (key) => (el) => { refs.current[key] = el; };

  const brands = useOptions(`/api/categories/${category}/brands?all=true`);
  const models = useOptions(
    brandId ? `/api/categories/${category}/brands/${brandId}/models` : null
  );

  const heardBrand = candidates[0]?.brand ?? value("brand");
  const heardModel = candidates[0]?.model ?? value("model");

  // By ID when the candidate carries one — that removes the spelling-mismatch
  // class entirely. The name match is the fallback for candidates from before
  // the backend started sending ids; the names are kept for display either way.
  const matchByName = (list, name) =>
    name ? list.find((o) => o.name.toLowerCase() === String(name).toLowerCase()) : undefined;
  const idOf = (list, explicit, name) => {
    if (explicit != null) {
      const asString = String(explicit);
      return list.some((o) => o.id === asString) ? asString : null;
    }
    return matchByName(list, name)?.id ?? null;
  };

  useEffect(() => {
    if (brandId || !brands.length) return;
    const hit = idOf(brands, candidates[0]?.brandId, heardBrand);
    if (hit) setBrandId(hit);
  }, [brands, heardBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modelId || !models.length) return;
    const hit = idOf(models, candidates[0]?.modelId, heardModel);
    if (hit) setModelId(hit);
  }, [models, heardModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editing clears that field's complaint immediately; the marker follows the
  // value, so it flips live rather than only after extraction.
  const clearMissing = (key) => setMissing((m) => m.filter((k) => k !== key));
  const set = (key) => (next) => {
    setDraft((d) => ({ ...d, [key]: next }));
    clearMissing(key);
  };

  // A reference that contradicts the brand above it is worse than none, so
  // changing brand or model drops the pick rather than leaving it stale.
  const changeBrand = (next) => {
    setBrandId(next ?? "");
    setModelId("");
    setPicked(null);
    clearMissing("brand");
  };
  const changeModel = (next) => {
    setModelId(next ?? "");
    setPicked(null);
    clearMissing("model");
  };
  // Picking a reference fills Brand and Model from that product. The reference
  // IS the identity — it names exactly one product — so its brand and model are
  // facts, not guesses, and leaving them on "still needed" asked the rep to
  // re-enter something they had just chosen.
  //
  // `setBrandId` directly, NOT `changeBrand`: that handler clears the pick (a
  // brand that contradicts the reference is worse than none), which would throw
  // away the selection being made right here.
  //
  // Model is deferred. `models` is fetched per brand, so when the brand also
  // changes the new list has not arrived yet and the id cannot be resolved
  // against it. The pending target is resolved by the effect below once it has.
  const changeReference = (next) => {
    setPicked(next);
    clearMissing("reference");
    if (!next) return;

    const brandHit = idOf(brands, next.brandId, next.brand);
    if (brandHit && brandHit !== brandId) {
      setBrandId(brandHit);
      setModelId("");
      clearMissing("brand");
    }

    if (next.modelId != null || next.model) {
      setPendingModel({ id: next.modelId ?? null, name: next.model ?? null });
    }
  };

  // Resolves the model left pending by a reference pick, once the list for its
  // brand has loaded. Cleared either way, so a model the catalogue does not
  // carry does not sit here waiting for a list that will never contain it.
  useEffect(() => {
    if (!pendingModel || !models.length) return;
    const hit = idOf(models, pendingModel.id, pendingModel.name);
    if (hit) {
      setModelId(hit);
      clearMissing("model");
    }
    setPendingModel(null);
  }, [models, pendingModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered by brand NAME, which is what a candidate carries.
  const brandName = brands.find((b) => b.id === brandId)?.name;
  const visible = useMemo(
    () =>
      brandName
        ? all.filter((c) => !c.brand || c.brand.toLowerCase() === brandName.toLowerCase())
        : all,
    [all, brandName]
  );
  const confident = visible.filter((c) => !c.loose);
  const weak = visible.filter((c) => c.loose);

  // ⚠️ The product triple sent to the API is derived ENTIRELY from the selected
  // candidate. ProductService::findOrCreate ignores the id we send and matches
  // on category_id + brand_id + model_id + reference_number (plus, for an admin,
  // the full image set) — so a payload mixing the candidate's reference with the
  // DROPDOWN's brand or model describes a product that does not exist, and the
  // backend silently CREATES it. The dropdowns correct what is displayed and
  // feed the manual route; they never reach this object.
  //
  // When the candidate carries no brand_id/model_id yet, its names are resolved
  // against the catalogue lists. If that resolution fails we refuse to send
  // rather than guess — see onCreate.
  const product = useMemo(() => {
    if (!picked) return null;
    const brand = idOf(brands, picked.brandId, picked.brand);
    const model = idOf(models, picked.modelId, picked.model);
    if (!brand || !model) return null;
    return {
      id: picked.id,
      category_id: picked.categoryId ?? category,
      brand_id: Number(brand),
      model_id: Number(model),
      reference_number: picked.reference,
      images: picked.imageIds ?? [],
    };
  }, [picked, brands, models]); // eslint-disable-line react-hooks/exhaustive-deps

  const filled = {
    brand: !!brandId,
    model: !!modelId,
    reference: !!picked,
    condition: !!draft.condition,
    box: !!draft.box,
    availability: !!draft.availability,
  };

  const onCreate = async () => {
    const gaps = MANDATORY.filter((k) => !filled[k]);
    if (gaps.length) {
      // Nothing is created and nothing is sent: the first gap is brought into
      // view and every gap is called out in words, not by colour.
      setMissing(gaps);
      const first = refs.current[gaps[0]];
      first?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      first?.focus?.({ preventScroll: true });
      return;
    }

    if (!product) {
      // Better a dead end the user can escape than a duplicate product in the
      // live catalogue.
      setApiError(
        "We couldn't confirm which catalogue product this is. Pick the reference again, or use Search manually."
      );
      return;
    }

    setSaving(true);
    setApiError(null);
    try {
      const listing = await createDictatedListing({
        condition: draft.condition,
        box: draft.box,
        availability: draft.availability,
        year: draft.year || undefined,
        currency: draft.currency,
        // MAJOR units in, ×100 applied once by processSubmitValues.
        price: draft.price === "" ? undefined : Number(draft.price),
        // Composed silently and never surfaced: the front end requires it even
        // on a listing that never reaches eBay.
        // Composed from the candidate too, for the same reason.
        ebay_title: [picked.brand, picked.model, picked.reference]
          .filter(Boolean)
          .join(" ")
          .slice(0, 80),
        // Category and images are carried from the chosen candidate, never asked for.
        images: picked.imageIds ?? [],
        product,
      });
      setCreated(listing);
      onCreated?.(listing, picked, all.indexOf(picked));
    } catch (error) {
      // Whatever the API objects to, it is said plainly. We do not invent new
      // required fields in the UI off the back of a rejection.
      const body = error?.response?.data;
      const firstBagMessage = body?.errors && Object.values(body.errors)[0]?.[0];
      setApiError(firstBagMessage || body?.message || "The listing could not be created.");
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <div className="rc-dictrev__done" role="status">
        <p className="rc-dictrev__done-title">Created!</p>
        <Button
          variant="primary"
          data-btn="listing-open"
          onClick={() => onViewListing(created)}
        >
          View listing
        </Button>
      </div>
    );
  }

  return (
    <div className="rc-dictrev">
      <div className="rc-dictrev__grid">
        <Choice
          id="rc-dictrev-brand"
          label={LABELS.brand}
          options={brands}
          value={brandId}
          onChange={changeBrand}
          required
          missing={missing.includes("brand")}
          fieldRef={bindRef("brand")}
        />
        <Choice
          id="rc-dictrev-model"
          label={LABELS.model}
          options={models}
          value={modelId}
          onChange={changeModel}
          required
          missing={missing.includes("model")}
          fieldRef={bindRef("model")}
        />
      </div>

      {/* Directly below Model: it is the field the whole match hangs on. */}
      <FieldShell
        id="rc-dictrev-reference"
        label={LABELS.reference}
        required
        filled={!!picked}
        missing={missing.includes("reference")}
      >
        {/* A dropdown that carries thumbnails: a native <option> cannot hold an
            image, so this is an ARIA combobox. Nothing is preselected — the top
            candidate is first because it scored highest, not because it is
            chosen. */}
        <div ref={bindRef("reference")}>
          <ReferenceSelect
            id="rc-dictrev-reference"
            candidates={confident}
            loose={weak}
            value={picked}
            onChange={changeReference}
          />
        </div>
        {confident.length === 0 && weak.length === 0 && (
          <p className="rc-dictrev__none">
            Nothing in the catalogue matched. Search for the watch manually — what was
            heard still carries over.
          </p>
        )}
      </FieldShell>

      <div className="rc-dictrev__grid">
        <Choice
          id="rc-dictrev-condition"
          label={LABELS.condition}
          options={conditionOptions}
          value={draft.condition}
          onChange={set("condition")}
          required
          missing={missing.includes("condition")}
          fieldRef={bindRef("condition")}
        />
        <Choice
          id="rc-dictrev-box"
          label={LABELS.box}
          options={boxOptions}
          value={draft.box}
          onChange={set("box")}
          required
          missing={missing.includes("box")}
          fieldRef={bindRef("box")}
        />
        <Choice
          id="rc-dictrev-availability"
          label={LABELS.availability}
          options={DataHelper.availability}
          value={draft.availability}
          onChange={set("availability")}
          required
          missing={missing.includes("availability")}
          fieldRef={bindRef("availability")}
        />
        <Choice
          id="rc-dictrev-year"
          label="Year"
          options={yearOptions}
          value={draft.year}
          onChange={set("year")}
        />
        <Choice
          id="rc-dictrev-currency"
          label="Currency"
          options={currencyOptions()}
          value={draft.currency}
          onChange={set("currency")}
        />
        <FieldShell id="rc-dictrev-price" label="Price" filled={!!draft.price}>
          <input
            id="rc-dictrev-price"
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            className="rc-dictrev__select"
            value={draft.price}
            placeholder="Optional"
            onChange={(e) => set("price")(e.target.value)}
          />
        </FieldShell>
      </div>

      {apiError && (
        <p className="rc-dictrev__api-error" role="alert">{apiError}</p>
      )}

      <ButtonGroup align="stretch">
        <Button
          variant="primary"
          data-btn="listing-create"
          disabled={saving}
          onClick={onCreate}
        >
          {saving ? "Creating…" : "Create listing"}
        </Button>
        <Button
          variant="secondary"
          data-btn="listing-search"
          onClick={() => onSearchManually(result)}
        >
          Search manually
        </Button>
      </ButtonGroup>
    </div>
  );
};

export default DictationReview;
